import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { usePostHog } from '@posthog/react';
import FundingCard from './FundingCard';
import FundingRow from './FundingRow';
import FundingFilters from './FundingFilters';
import CompatibilityModem from './CompatibilityModem';
import LoadingSpinner from '../common/LoadingSpinner';
import StatusPieChart from '../common/StatusPieChart';
import Pagination from '../common/Pagination';
import { trackSearch } from '../../utils/analytics';
import { FUNDING_TYPES } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../auth/AuthModal';
import AlertButton from './AlertButton';

// Map profile country names to API location filter values
const countryToLocationValue = (country) => country.toLowerCase().replace(/ /g, '_');

const SmartSearchBar = ({ onSearch, onFilterChange, searchQuery, setSearchQuery, onRequireAuth }) => {
  const posthogSearch = usePostHog();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [canScrollMore, setCanScrollMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [capped, setCapped] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const suggestionsRef = useRef(null);
  const filterOptions = useRef({ aims: [], interests: [], land_types: [] });
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    let cancelled = false;
    api.get('/funding-schemes/available-filters/').then(res => {
      if (cancelled) return;
      const d = res.data;
      filterOptions.current = {
        aims: (d.aims || []).map(v => ({ value: v, label: v })),
        interests: (d.interests || []).map(v => ({ value: v, label: v })),
        land_types: (d.land_types || []).map(v => ({ value: v, label: v })),
      };
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setShowAll(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    // Suggestions reveal scheme names, so they're for signed-in users only
    if (!isAuthenticated || !searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      setTotalCount(0);
      setCapped(false);
      return;
    }

    const SUGGESTION_CAP = 25;
    const LOCAL_CAP = 3;
    const q = searchQuery.toLowerCase();

    const allAims = filterOptions.current.aims
      .filter(a => a.label.toLowerCase().includes(q))
      .map(a => ({ type: 'aim', label: a.value }));

    const allInterests = filterOptions.current.interests
      .filter(i => i.label.toLowerCase().includes(q))
      .map(i => ({ type: 'interest', label: i.value }));

    const allLandTypes = filterOptions.current.land_types
      .filter(l => l.label.toLowerCase().includes(q))
      .map(l => ({ type: 'land_type', label: l.value }));

    const allLocalMatches = [...allAims, ...allInterests, ...allLandTypes];
    const localMatches = showAll
      ? allLocalMatches
      : [...allAims.slice(0, LOCAL_CAP), ...allInterests.slice(0, LOCAL_CAP), ...allLandTypes.slice(0, LOCAL_CAP)];

    const timer = setTimeout(async () => {
      try {
        const url = `/funding-schemes/search-suggestions/?q=${encodeURIComponent(searchQuery)}${showAll ? '&show_all=true' : ''}`;
        const response = await api.get(url);
        const apiSuggestions = response.data.suggestions || [];
        const apiTotalCount = response.data.total_count ?? apiSuggestions.length;
        const fullTotal = allLocalMatches.length + apiTotalCount;

        setTotalCount(fullTotal);

        if (showAll || fullTotal <= SUGGESTION_CAP) {
          setSuggestions([...localMatches, ...apiSuggestions]);
          setCapped(false);
        } else {
          setSuggestions([...localMatches, ...apiSuggestions].slice(0, SUGGESTION_CAP));
          setCapped(true);
        }
      } catch (error) {
        console.error('Failed to load suggestions:', error);
        setSuggestions(localMatches);
        setTotalCount(allLocalMatches.length);
        setCapped(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, showAll, isAuthenticated]);

  useEffect(() => {
    const el = suggestionsRef.current;
    if (!el) { setCanScrollMore(false); return; }
    const check = () => setCanScrollMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
    check();
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, [suggestions, showSuggestions]);

  const handleSuggestionClick = (suggestion, e) => {
    if (suggestion.type === 'scheme') {
      setSearchQuery(suggestion.label);
      onSearch(suggestion.label);
      setShowSuggestions(false);
      return;
    }

    posthogSearch.capture('search_suggestion_selected', {
      suggestion_type: suggestion.type,
      suggestion_label: suggestion.label,
      query: searchQuery,
    });

    if (suggestion.type === 'funder') {
      onFilterChange({ funders: [suggestion.id] });
      setSearchQuery('');
      setShowSuggestions(false);
      return;
    }

    if (suggestion.type === 'fund_collection') {
      onFilterChange({ fund_collection: [suggestion.id] });
      setSearchQuery('');
      setShowSuggestions(false);
      return;
    }

    if (suggestion.type === 'aim') {
      onFilterChange({ aim: [suggestion.label] });
      setSearchQuery('');
      setShowSuggestions(false);
      return;
    }

    if (suggestion.type === 'interest') {
      onFilterChange({ interests: [suggestion.label] });
      setSearchQuery('');
      setShowSuggestions(false);
      return;
    }

    if (suggestion.type === 'land_type') {
      onFilterChange({ land_types: [suggestion.label] });
      setSearchQuery('');
      setShowSuggestions(false);
      return;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Let anonymous users type, but require sign-up to see results
    if (!isAuthenticated) {
      if (typeof onRequireAuth === 'function') onRequireAuth();
      return;
    }
    
    onSearch(searchQuery);
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch(''); // Trigger search with empty query
    setShowSuggestions(false);
  };

  const typeLabels = {
    scheme: 'Opportunity',
    funder: 'Funder',
    fund_collection: 'Collection',
    aim: 'Aim',
    interest: 'Interest',
    land_type: 'Land Type',
  };

  const typeColors = {
    scheme: 'bg-blue-100 text-blue-700',
    funder: 'bg-green-100 text-green-700',
    fund_collection: 'bg-purple-100 text-purple-700',
    aim: 'bg-lime-100 text-lime-700',
    interest: 'bg-teal-100 text-teal-700',
    land_type: 'bg-amber-100 text-amber-700',
  };

  return (
    <form onSubmit={handleSubmit} className="relative w-full mb-6">
      {/* Search Input */}
      <div className="relative flex">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search funding opportunities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="block w-full pl-10 pr-24 py-3 border border-gray-300 rounded-l-lg leading-5 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 bg-white"
        />
        <button
          type="submit"
          className="px-6 py-3 bg-black hover:bg-gray-800 text-white font-medium rounded-r-lg border border-black focus:outline-none transition-colors"
        >
          Search
        </button>
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-20 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg" onMouseDown={(e) => e.preventDefault()}>
          <div className="relative">
            <div ref={suggestionsRef} className="py-1 max-h-72 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="relative flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  onClick={(e) => handleSuggestionClick(suggestion, e)}
                >

                  <span>
                    {suggestion.label}
                    {suggestion.type === 'scheme' && suggestion.fund_collection && (
                      <span className="ml-2 text-xs text-purple-600 font-medium">{suggestion.fund_collection}</span>
                    )}
                  </span>

                  <span className="relative z-10 flex items-center gap-2 shrink-0 ml-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[suggestion.type]}`}>
                      {typeLabels[suggestion.type]}
                    </span>
                    {suggestion.type === 'scheme' && suggestion.id && (
                      <AlertButton schemeId={suggestion.id} initialActive={suggestion.is_alerted} />
                    )}
                    {suggestion.type === 'fund_collection' && suggestion.id && (
                      <AlertButton collectionId={suggestion.id} initialActive={suggestion.is_alerted} />
                    )}
                    {suggestion.type === 'funder' && suggestion.id && (
                      <AlertButton funderId={suggestion.id} initialActive={suggestion.is_alerted} />
                    )}
                    {suggestion.type === 'scheme' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          posthogSearch.capture('compatible_schemes_searched', {
                            funding_id: suggestion.id,
                            funding_name: suggestion.label,
                            source: 'search_suggestion',
                          });
                          onFilterChange({ compatible_with: { id: suggestion.id, name: suggestion.label, fund_collection: suggestion.fund_collection } });
                          setSearchQuery('');
                          setShowSuggestions(false);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        title="Find compatible opportunities"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </svg>
                      </button>
                    )}
                    {suggestion.blog_slug && (
                      <a
                        href={`/blog/${suggestion.blog_slug}`}
                        onClick={(e) => { e.stopPropagation(); setShowSuggestions(false); }}
                        className="text-gray-400 hover:text-gray-600"
                        title="View blog post"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </a>
                    )}
                  </span>
                </div>
              ))}
            </div>
            {canScrollMore && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex flex-col items-center">
                <div className="w-full h-8 bg-gradient-to-t from-white to-transparent" />
                <span className="absolute bottom-1.5 text-[11px] text-gray-400 flex items-center gap-1">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  more results
                </span>
              </div>
            )}
          </div>
          {capped && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 border-t border-gray-200 text-center rounded-b-lg"
            >
              Show all {totalCount} results
            </button>
          )}
        </div>
      )}
    </form>
  );
};

const ViewToggle = ({ viewMode, setViewMode }) => {
  return (
    <div className="inline-flex items-center border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setViewMode('list')}
        className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-colors ${
          viewMode === 'list'
            ? 'bg-gray-900 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        title="List view"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <button
        onClick={() => setViewMode('card')}
        className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-colors ${
          viewMode === 'card'
            ? 'bg-gray-900 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        title="Card view"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
    </div>
  );
};

const FundingListResults = React.memo(({
  fundings,
  loading,
  error,
  hasActiveSearch,
  searchTerms,
  searchQuery,
  pagination,
  onPageChange,
  onClearSearch,
  viewMode,
  onCompatibilityFilter,
}) => {
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
        {error}
      </div>
    );
  }

  // Common pagination component props
  const paginationProps = {
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
    totalItems: pagination.count,
    itemsPerPage: pagination.pageSize,
    onPageChange: onPageChange,
    loading: loading
  };

  return (
    <>
      {/* Results count and search status */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          {hasActiveSearch && (
            <div className="flex items-center text-sm text-green-700 bg-green-50 px-3 py-1 rounded-full">
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search active: "{searchQuery}"
            </div>
          )}
        </div>
        {loading && <LoadingSpinner size="small" />}
      </div>

      {/* Funding cards or rows */}
      {fundings.length > 0 ? (
        <>
          {/* Top Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mb-6">
              <Pagination {...paginationProps} position="top" />
            </div>
          )}
          
          {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {fundings.map((funding) => (
                <FundingCard key={funding.id} funding={funding} onCompatibilityFilter={onCompatibilityFilter} />
              ))}
            </div>
          ) : (
            <div className="space-y-0 mb-8 border-2 border-gray-100 rounded-lg overflow-hidden divide-y divide-gray-100">
              {fundings.map((funding) => (
                <FundingRow key={funding.id} funding={funding} onCompatibilityFilter={onCompatibilityFilter} />
              ))}
            </div>
          )}
          
          {/* Bottom Pagination */}
          {pagination.totalPages > 1 && (
            <Pagination {...paginationProps} position="bottom" />
          )}
        </>
      ) : !loading ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {hasActiveSearch ? 'No matching opportunities found' : 'No funding opportunities found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasActiveSearch 
              ? 'Try different search terms or adjust your filters to see more results.'
              : 'Try adjusting your filters to see more results.'
            }
          </p>
          {hasActiveSearch && (
            <button
              onClick={onClearSearch}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              Clear search and filters
            </button>
          )}
        </div>
      ) : null}
    </>
  );
});

const FundingList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const { isAuthenticated } = useAuth();

  // Restore saved filter state from sessionStorage (when no URL params)
  const savedState = (() => {
    if (location.search) return null;
    try {
      const saved = sessionStorage.getItem('fundingListState');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  // Initialize filters from URL params, falling back to saved state
  const getInitialFilters = () => {
    // If no URL params, restore from session storage
    if (!location.search && savedState?.filters) {
      return savedState.filters;
    }

    const urlParams = new URLSearchParams(location.search);
    const filters = {};

    const urlFundCollection = urlParams.get('fund_collection');
    const urlGroupBy = urlParams.get('group_by');
    const urlCompatibleWith = urlParams.get('compatible_with');
    const urlCompatibleName = urlParams.get('compatible_name');
    const urlOrdering = urlParams.get('ordering');

    if (urlFundCollection) {
      filters.fund_collection = urlFundCollection.split(',').map(id => parseInt(id, 10)).filter(Boolean);
    }
    if (urlCompatibleWith) {
      const ids = urlCompatibleWith.split(',').map(id => parseInt(id, 10)).filter(Boolean);
      const names = urlCompatibleName ? urlCompatibleName.split('|').map(n => decodeURIComponent(n)) : [];
      filters.compatible_with = ids.map((id, i) => ({ id, ...(names[i] ? { name: names[i] } : {}) }));
    }
    if (urlGroupBy) {
      filters.group_by = urlGroupBy;
    } else {
      filters.group_by = 'funder'; // default
    }
    if (urlOrdering) {
      filters.ordering = urlOrdering;
    }

    // Integer-ID array filters
    const urlFunders = urlParams.get('funders');
    if (urlFunders) {
      filters.funders = urlFunders.split(',').map(id => parseInt(id, 10)).filter(Boolean);
    }

    // String array filters
    const stringArrayKeys = ['farming_type', 'competitive', 'status', 'funding_type', 'land_types', 'aim', 'interests', 'protected_land'];
    stringArrayKeys.forEach(key => {
      const val = urlParams.get(key);
      if (val) {
        filters[key] = val.split(',');
      }
    });

    // Protected land threshold (default is 'eligible', only stored if non-default)
    const urlThreshold = urlParams.get('protected_land_threshold');
    if (urlThreshold) {
      filters.protected_land_threshold = urlThreshold;
    }

    return filters;
  };
  
  const [fundings, setFundings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getInitialFilters());
  const initialSearch = (location.search && new URLSearchParams(location.search).get('search')) || savedState?.activeSearchQuery || '';
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [activeSearchQuery, setActiveSearchQuery] = useState(initialSearch);
  const [showStackFilterReminder, setShowStackFilterReminder] = useState(false);
  const [searchTerms, setSearchTerms] = useState([]);
  const [viewMode, setViewMode] = useState(savedState?.viewMode || 'list');
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: (location.search && parseInt(new URLSearchParams(location.search).get('page') || '1', 10)) || 1,
    totalPages: 1,
    pageSize: 24
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authAction, setAuthAction] = useState('search and filter funding opportunities');
  // Anonymous users can pick filters, but they're held here and not applied until sign-in
  const [draftFilters, setDraftFilters] = useState({});
  const [draftLocation, setDraftLocation] = useState(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [locationPopupOpen, setLocationPopupOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const expandedGroupsRef = useRef({});
  const [groupedResults, setGroupedResults] = useState(null);
  const [groupSchemes, setGroupSchemes] = useState({});       // cached schemes per group
  const [loadingGroups, setLoadingGroups] = useState({});      // loading state per group
  const [groupsOrder, setGroupsOrder] = useState(null);        // server-provided group ordering
  const [locationFilter, setLocationFilter] = useState(() => {
    if (location.search) {
      const urlParams = new URLSearchParams(location.search);
      const urlLocation = urlParams.get('location');
      const urlCounties = urlParams.get('counties');
      if (urlLocation || urlCounties) {
        return {
          country: urlLocation ? urlLocation.split(',') : [],
          counties: urlCounties ? urlCounties.split(',') : [],
        };
      }
    }
    return savedState?.locationFilter || { country: [], counties: [] };
  });
  const [locationLoaded] = useState(true);
  const preserveExpandedRef = useRef(false);  // true only when adding to the stack

  // Keep ref in sync so fetchFundings can read current expanded groups
  // without adding expandedGroups to its dependency array
  useEffect(() => {
    expandedGroupsRef.current = expandedGroups;
  }, [expandedGroups]);


  useEffect(() => {
    posthog.capture('funding_list_viewed');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist filter state to sessionStorage so it survives navigation to detail and back
  useEffect(() => {
    sessionStorage.setItem('fundingListState', JSON.stringify({
      filters,
      activeSearchQuery,
      locationFilter,
      viewMode,
    }));
  }, [filters, activeSearchQuery, locationFilter, viewMode]);

  // Sync all filter/search/page state to URL query params
  useEffect(() => {
    const params = new URLSearchParams();

    // Search
    if (activeSearchQuery.trim()) params.set('search', activeSearchQuery.trim());

    // Page (omit default)
    if (pagination.currentPage > 1) params.set('page', pagination.currentPage.toString());

    // Ordering (omit if unset)
    if (filters.ordering) params.set('ordering', filters.ordering);

    // Group by (omit default 'fund_collection')
    if (filters.group_by && filters.group_by !== 'fund_collection') {
      params.set('group_by', filters.group_by);
    }

    // Fund collection
    if (filters.fund_collection?.length > 0) {
      params.set('fund_collection', filters.fund_collection.join(','));
    }

    // Compatibility stack
    if (filters.compatible_with?.length > 0) {
      params.set('compatible_with', filters.compatible_with.map(s => s.id).join(','));
      params.set('compatible_name', filters.compatible_with.map(s => encodeURIComponent(s.name || '')).join('|'));
    }

    // Funders (integer IDs)
    if (filters.funders?.length > 0) {
      params.set('funders', filters.funders.join(','));
    }

    // String array filters (omit if empty / default)
    const stringArrayKeys = ['farming_type', 'competitive', 'status', 'funding_type', 'land_types', 'aim', 'interests', 'protected_land'];
    stringArrayKeys.forEach(key => {
      if (filters[key]?.length > 0) {
        params.set(key, filters[key].join(','));
      }
    });

    // Protected land threshold (omit default 'eligible')
    if (filters.protected_land_threshold && filters.protected_land_threshold !== 'eligible') {
      params.set('protected_land_threshold', filters.protected_land_threshold);
    }

    // Location
    if (locationFilter.country?.length > 0) {
      params.set('location', locationFilter.country.join(','));
    }
    if (locationFilter.counties?.length > 0) {
      params.set('counties', locationFilter.counties.join(','));
    }

    const queryString = params.toString();
    const newSearch = queryString ? `?${queryString}` : '';
    if (newSearch !== location.search) {
      navigate(`${location.pathname}${newSearch}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, locationFilter, activeSearchQuery, pagination.currentPage]);


  const handleGroupSaveToggle = (groupName, fundingId, isSaved) => {
    setGroupSchemes(prev => {
      const schemes = prev[groupName];
      if (!schemes) return prev;
      return {
        ...prev,
        [groupName]: schemes.map(s =>
          s.id === fundingId ? { ...s, is_favourite: isSaved } : s
        ),
      };
    });
  };

  const toggleGroup = async (groupName) => {
    if (!isAuthenticated) {
      requireAuth('expand_group', { group_name: groupName, group_by: filters.group_by });
      return;
    }
    const isExpanding = !expandedGroups[groupName];
    setExpandedGroups(prev => ({ ...prev, [groupName]: isExpanding }));

    // Fetch schemes for this group if expanding and not already cached
    if (isExpanding && !groupSchemes[groupName]) {
      setLoadingGroups(prev => ({ ...prev, [groupName]: true }));
      try {
        const params = new URLSearchParams();
        if (activeSearchQuery.trim()) params.append('search', activeSearchQuery.trim());
        Object.entries(filters).forEach(([key, value]) => {
          if (key === 'compatible_with' && Array.isArray(value) && value.length > 0) {
            params.append(key, value.map(s => s.id).join(','));
          } else if (Array.isArray(value) && value.length > 0) {
            params.append(key, value.join(','));
          } else if (!Array.isArray(value) && value) {
            params.append(key, value);
          }
        });
        // Apply profile location filters
        if (locationFilter.country.length > 0) {
          params.append('location', locationFilter.country.map(countryToLocationValue).join(','));
        }
        if (locationFilter.counties.length > 0) {
          params.append('counties', locationFilter.counties.join(','));
        }
        params.append('group_value', groupName);
        const response = await api.get(`/funding-schemes/?${params.toString()}`);
        setGroupSchemes(prev => ({ ...prev, [groupName]: response.data.schemes }));
      } catch (err) {
        console.error(`Failed to load schemes for group "${groupName}":`, err);
      } finally {
        setLoadingGroups(prev => ({ ...prev, [groupName]: false }));
      }
    }
  };
  
  const fetchFundings = useCallback(async (pageNumber = null, signal = null) => {
    if (!locationLoaded) return;
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();

      // Add search query
      if (activeSearchQuery.trim()) {
        params.append('search', activeSearchQuery.trim());
        setSearchTerms(activeSearchQuery.trim().split(/\s+/));
      } else {
        setSearchTerms([]);
      }

      // Add ALL filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'compatible_with' && Array.isArray(value) && value.length > 0) {
          params.append(key, value.map(s => s.id).join(','));
        } else if (Array.isArray(value) && value.length > 0) {
          params.append(key, value.join(','));
        } else if (!Array.isArray(value) && value) {
          params.append(key, value);
        }
      });

      // Apply profile location filters
      if (locationFilter.country.length > 0) {
        params.append('location', locationFilter.country.map(countryToLocationValue).join(','));
      }
      if (locationFilter.counties.length > 0) {
        params.append('counties', locationFilter.counties.join(','));
      }

      // If NOT grouping: Add page number
      if (!filters.group_by) {
        const currentPage = pageNumber || pagination.currentPage;
        if (currentPage > 1) {
          params.append('page', currentPage.toString());
        }
      }

      const endpoint = `/funding-schemes/?${params.toString()}`;
      const response = await api.get(endpoint, { signal });

      // Handle grouped vs normal response
      if (filters.group_by && response.data.groups) {
        // Grouped summary response (counts + statuses only)
        setGroupedResults(response.data.groups);
        setGroupsOrder(response.data.groups_order || null);
        setGroupSchemes({});     // clear stale cached schemes
        setFundings([]);
        setPagination({
          count: response.data.count,
          next: null,
          previous: null,
          currentPage: 1,
          totalPages: 1,
          pageSize: response.data.count
        });

        // Only preserve expanded groups when adding to the stack.
        // For regular filter changes, collapse all groups so stale schemes don't persist.
        const shouldPreserve = preserveExpandedRef.current;
        preserveExpandedRef.current = false;

        if (shouldPreserve) {
          // Keep previously-expanded groups open (if they still exist in the new results)
          // and re-fetch their schemes with the updated filters.
          const prevExpanded = expandedGroupsRef.current;
          const newGroupNames = new Set(Object.keys(response.data.groups));
          const groupsToRefresh = Object.keys(prevExpanded).filter(
            name => prevExpanded[name] && newGroupNames.has(name)
          );
          setExpandedGroups(prev => {
            const kept = {};
            Object.keys(prev).forEach(name => {
              if (prev[name] && newGroupNames.has(name)) {
                kept[name] = true;
              }
            });
            return kept;
          });
          if (groupsToRefresh.length > 0) {
            const loadingUpdate = {};
            groupsToRefresh.forEach(name => { loadingUpdate[name] = true; });
            setLoadingGroups(prev => ({ ...prev, ...loadingUpdate }));

            groupsToRefresh.forEach(async (groupName) => {
              try {
                const groupParams = new URLSearchParams(params);
                groupParams.append('group_value', groupName);
                const groupResp = await api.get(`/funding-schemes/?${groupParams.toString()}`, { signal });
                if (signal?.aborted) return;
                setGroupSchemes(prev => ({ ...prev, [groupName]: groupResp.data.schemes }));
              } catch (err) {
                if (err.name === 'AbortError' || signal?.aborted) return;
                console.error(`Failed to refresh schemes for group "${groupName}":`, err);
              } finally {
                if (!signal?.aborted) {
                  setLoadingGroups(prev => ({ ...prev, [groupName]: false }));
                }
              }
            });
          }
        } else {
          // Regular filter change — collapse all groups
          setExpandedGroups({});
        }
      } else {
        // Normal paginated response
        trackSearch(params.toString(), response.data.results);
        setFundings(response.data.results);
        setGroupedResults(null);
        
        const totalItems = response.data.count;
        const pageSize = 24;
        const totalPages = Math.ceil(totalItems / pageSize);
        const currentPage = pageNumber || pagination.currentPage;
        
        setPagination({
          count: totalItems,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: currentPage,
          totalPages: totalPages,
          pageSize: pageSize
        });
      }
    } catch (err) {
      if (err.name === 'AbortError' || signal?.aborted) return;
      setError('Failed to load funding opportunities. Please try again.');
      console.error('Error fetching fundings:', err);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearchQuery, filters, locationFilter, locationLoaded]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchFundings(pagination.currentPage, abortController.signal);
    return () => abortController.abort();
  }, [fetchFundings, pagination.currentPage]);

  const handleSearch = (query) => {
    if (query.trim()) {
      posthog.capture('funding_searched', { query: query.trim(), query_length: query.trim().length });
    }
    // If clearing search, remove ordering first
    if (!query.trim() && filters.ordering === '-_search_score') {
      setFilters(prev => {
        const { ordering, ...rest } = prev;
        return rest;
      });
    }
    
    // If starting a search, clear ordering to default relevance
    if (query.trim() && filters.ordering) {
      setFilters(prev => {
        const { ordering, ...rest } = prev;
        return rest;
      });
    }
    
    setActiveSearchQuery(query);

    // Reset to page 1 when searching
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Show the sign-up modal and record what the anonymous user was trying to find
  const requireAuth = (trigger, extra = {}) => {
    const actions = {
      search: 'see your search results',
      show_results: 'see your filtered results',
      expand_group: 'view the opportunities in this group',
    };
    posthog.capture('signup_gate_shown', {
      trigger,
      search_query: searchQuery.trim() || undefined,
      filters: { ...filters, ...draftFilters },
      location: draftLocation || locationFilter,
      ...extra,
    });
    setAuthAction(actions[trigger] || 'search and filter funding opportunities');
    setShowAuthModal(true);
  };

  const handleFilterChange = (newFilters) => {
    if (!isAuthenticated) {
      setDraftFilters(prev => ({ ...prev, ...newFilters }));
      return;
    }
    // Only preserve expanded groups when adding to the stack, not for regular filter changes
    const isStackChange = 'compatible_with' in newFilters && newFilters.compatible_with !== undefined;
    preserveExpandedRef.current = isStackChange;

    // Check if this is the first scheme being added to a stack
    const isStartingStack = isStackChange &&
                            (!filters.compatible_with || filters.compatible_with.length === 0);

    if (isStartingStack) {
      // Auto-clear search when starting a stack
      setSearchQuery('');
      setActiveSearchQuery('');

      // Show reminder if there are active content filters
      const hasContentFilters = Object.entries(filters).some(([key, value]) =>
        key !== 'group_by' && key !== 'ordering' && key !== 'compatible_with' &&
        (Array.isArray(value) ? value.length > 0 : value)
      );
      if (hasContentFilters) {
        setShowStackFilterReminder(true);
      }
    }

    setFilters(prev => {
      const merged = { ...prev, ...newFilters };
      if ('compatible_with' in newFilters && newFilters.compatible_with !== undefined) {
        const existing = prev.compatible_with || [];
        // Normalise to object shape in case a bare id slips through
        const raw = newFilters.compatible_with;
        const incoming = (typeof raw === 'object' && !Array.isArray(raw)) ? raw : { id: raw };
        merged.compatible_with = [...existing, incoming];
      }
      return merged;
    });
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    if (!isAuthenticated) {
      // Only drafts exist for anonymous users; keep the applied (grouped) view intact
      setDraftFilters({});
      setDraftLocation(null);
      setSearchQuery('');
      return;
    }
    setFilters({});
    setSearchQuery('');
    setActiveSearchQuery('');
    setShowStackFilterReminder(false);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setFilters(prev => {
      const { ordering, ...rest } = prev;
      return rest;
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearContentFilters = () => {
    setFilters(prev => {
      const kept = {};
      if (prev.compatible_with) kept.compatible_with = prev.compatible_with;
      if (prev.group_by) kept.group_by = prev.group_by;
      if (prev.ordering) kept.ordering = prev.ordering;
      return kept;
    });
    setShowStackFilterReminder(false);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));

    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveSearch = activeSearchQuery.trim().length > 0;

  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (key === 'group_by' || key === 'ordering' || key === 'compatible_with') return count;
    if (Array.isArray(value)) return count + value.length;
    if (value) return count + 1;
    return count;
  }, 0);

  if ((loading && fundings.length === 0) && !groupedResults) {
    return <LoadingSpinner size="large" />;
  }

  const filterProps = {
    filters: isAuthenticated ? filters : { ...filters, ...draftFilters },
    onFilterChange: handleFilterChange,
    onClearFilters: handleClearFilters,
    resultsCount: pagination.count,
    currentResultsLength: groupedResults
      ? Object.values(groupedResults).reduce((sum, g) => sum + (g.count || 0), 0)
      : (fundings?.length || 0),
    groupCount: groupedResults ? Object.keys(groupedResults).length : 0,
    hasActiveSearch,
    searchQuery: activeSearchQuery,
    onClearSearch: handleClearSearch,
    loading,
    locationFilter: isAuthenticated ? locationFilter : (draftLocation || locationFilter),
    onLocationFilterChange: isAuthenticated
      ? setLocationFilter
      : (update) => setDraftLocation(prev => (typeof update === 'function' ? update(prev || locationFilter) : update)),
    locationPopupOpen,
    onLocationPopupToggle: setLocationPopupOpen,
    isAuthenticated,
    onRequireAuth: () => requireAuth('filter_control'),
    onShowResults: isAuthenticated ? undefined : () => requireAuth('show_results'),
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">

      {/* Header with View Toggle */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Find Opportunities</h1>
        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
      </div>

      {/* Smart Search Bar */}
      <SmartSearchBar
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onRequireAuth={() => requireAuth('search')}
      />


      {/* Mobile/tablet toolbar — hidden on desktop */}
      <div className="lg:hidden flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-800">
          {groupedResults
            ? `${pagination.count} opportunities`
            : pagination.count > 0
              ? `${fundings.length} of ${pagination.count}`
              : 'No results'}
        </p>
        <button
          onClick={() => setFilterSheetOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] border-2 border-black rounded-md text-sm font-semibold bg-white hover:bg-black hover:text-white transition-colors"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
          </svg>
          Sort &amp; Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-[#615DEC] text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Desktop inline filters — hidden on mobile/tablet */}
      <div className="hidden lg:block">
        <FundingFilters {...filterProps} />
      </div>

      {/* Mobile/tablet filter bottom sheet */}
      {filterSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setFilterSheetOpen(false)}
          />
          <div className="relative bg-white rounded-t-2xl max-h-[88vh] flex flex-col">
            <div className="sticky top-0 bg-white rounded-t-2xl flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-900">Sort &amp; Filters</h2>
              <button
                onClick={() => setFilterSheetOpen(false)}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close filters"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-4">
              <FundingFilters {...filterProps} sheetMode={true} />
            </div>
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-white">
              <button
                onClick={() => {
                  setFilterSheetOpen(false);
                  if (!isAuthenticated) requireAuth('show_results');
                }}
                className="w-full py-3 min-h-[44px] bg-black text-white font-semibold rounded-lg text-sm hover:bg-gray-800 transition-colors"
              >
                Show results{pagination.count > 0 && ` (${pagination.count})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stack filter reminder — shown when starting a stack with active filters */}
      {showStackFilterReminder && activeFilterCount > 0 && filters.compatible_with?.length > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <svg className="h-4 w-4 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-amber-800">You have filters active that may limit your stack results</span>
          <button
            type="button"
            onClick={handleClearContentFilters}
            className="text-sm font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 whitespace-nowrap"
          >
            Clear filters
          </button>
          <button
            type="button"
            onClick={() => setShowStackFilterReminder(false)}
            className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded-full text-amber-400 hover:text-amber-600 hover:bg-amber-100"
            aria-label="Dismiss reminder"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <CompatibilityModem
        stack={filters.compatible_with || []}
        compatibleCount={pagination.count}
        onRemove={(id) => setFilters(prev => ({
          ...prev,
          compatible_with: prev.compatible_with.filter(s => s.id !== id)
        }))}
        onClear={() => {
          setFilters(prev => {
            const { compatible_with, ...rest } = prev;
            return rest;
          });
          setShowStackFilterReminder(false);
        }}
      />

      {/* Results - Conditionally render grouped or normal */}
      {groupedResults ? (
        // Grouped display — summaries load instantly, schemes load on expand
        <div className="space-y-4">
          {(groupsOrder || Object.entries(groupedResults)
            // Sort groups alphabetically A→Z by default
            .sort((a, b) => a[0].localeCompare(b[0]))
            // Sort by count (most schemes first):
            // .sort((a, b) => (b[1].count || 0) - (a[1].count || 0))
            .map(([name]) => name)
          ).map((groupName) => {
            const summary = groupedResults[groupName];
            if (!summary) return null;
            const schemes = groupSchemes[groupName] || [];
            const isExpanded = expandedGroups[groupName];
            const isLoading = loadingGroups[groupName];

            return (
              <div key={groupName} className="border-2 border-gray-200 rounded-lg" style={{ overflow: 'visible' }}>
                <button
                  onClick={() => toggleGroup(groupName)}
                  className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors rounded-t-lg"
                  style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {/* Status pie chart */}
                    <StatusPieChart statuses={summary.statuses} size={24} />
                    <h3 className="text-lg font-semibold text-gray-900 truncate min-w-0">
                      {filters.group_by === 'funding_type'
                        ? (FUNDING_TYPES.find(t => t.value === groupName)?.label ?? groupName)
                        : groupName}
                    </h3>
                    {summary.funder && (
                      <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2.5 py-0.5 rounded-full flex-shrink-0">
                        {summary.funder}
                      </span>
                    )}
                    <span className="text-sm text-gray-500 bg-gray-200 px-3 py-1 rounded-full flex-shrink-0">
                      {summary.count}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {filters.group_by === 'fund_collection' && summary.collection_id && (
                      <AlertButton collectionId={summary.collection_id} />
                    )}
                    {filters.group_by === 'funder' && summary.funder_id && (
                      <AlertButton funderId={summary.funder_id} />
                    )}
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="bg-white">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner size="small" />
                      </div>
                    ) : schemes.length > 0 ? (
                      viewMode === 'card' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                          {schemes.map((funding) => (
                            <FundingCard
                              key={funding.id}
                              funding={funding}
                              searchTerms={searchTerms}
                              onCompatibilityFilter={(scheme) => handleFilterChange({ compatible_with: scheme })}
                              onSaveToggle={(fundingId, isSaved) => handleGroupSaveToggle(groupName, fundingId, isSaved)}
                            />
                          ))}
                        </div>
                      ) : (
                        <div>
                          {schemes.map((funding) => (
                            <FundingRow
                              key={funding.id}
                              funding={funding}
                              onCompatibilityFilter={(scheme) => handleFilterChange({ compatible_with: scheme })}
                              onSaveToggle={(fundingId, isSaved) => handleGroupSaveToggle(groupName, fundingId, isSaved)}
                            />
                          ))}
                        </div>
                      )
                    ) : (
                      <p className="text-center text-gray-500 py-6 text-sm">No opportunities found</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Normal paginated display
        <FundingListResults
          fundings={fundings}
          loading={loading}
          error={error}
          hasActiveSearch={hasActiveSearch}
          searchTerms={searchTerms}
          searchQuery={activeSearchQuery}
          pagination={pagination}
          onPageChange={handlePageChange}
          onClearSearch={handleClearSearch}
          viewMode={viewMode}
          onCompatibilityFilter={(scheme) => handleFilterChange({ compatible_with: scheme })}
        />
      )}

        {/* Global Auth Modal for this page */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
          }}
          action={authAction}
        />
      </div>
    </>
  );
};

export default FundingList;